
import { computed, defineComponent, onMounted, PropType, reactive, ref, toRefs } from 'vue'
import { Notify, QSpinnerTail, useQuasar } from 'quasar'
import { CtCatalogue } from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import DButton from '@/components/base/DButton.vue'
import { cloneDeep, isEqual } from 'lodash'
import PenIcon from '@/components/Icons/PenIcon.vue'
import { format } from '@/utils/template'
import { Status } from '@/models/status'
import { SyncActionType } from '@/models/interfaces/catalogue'
import InfoButton from '@/components/Icons/InfoButton.vue'
import ServiceType from '@/models/interfaces/service'
import magentoLogo from '@/assets/integrations/magento.png'
import miraklLogo from '@/assets/integrations/mirakl.png'
import bolcomLogo from '@/assets/integrations/bolcom.png'
import shopifyLogo from '@/assets/integrations/shopify.png'
import woocommerceLogo from '@/assets/integrations/woocommerce.png'
import directifyLogo from '@/assets/integrations/directify.png'
import IconSinerlog from '@/assets/icons/icon-sinerlog.png'
import lightspeedLogo from '@/assets/integrations/lightspeed.png'
import shopwareLogo from '@/assets/integrations/shopware.png'

import CryptoJS from 'crypto-js'
import { CHANNEL_API_KEY_PRE_ID } from './ChannelSettings.vue'
import { Tenant } from '@/pages/Settings/components/editors/TenantConfigEditor.vue'



const SERVICE_TYPES = [
  { label: 'Directify', value: 'directify', type: 'catalogue', logo: directifyLogo, apiVersion: 'Supplier', order: 'two-way', product: 'one-way', inventory: 'none', color: '#057E5E'},
  { label: 'Bol.com', value: 'bolcom', type: 'catalogue', logo: bolcomLogo, apiVersion: 'v10', order: 'two-way', product: 'two-way', inventory: 'one-way', color: '#0000E6' },
  { label: 'Shopify', value: 'shopify', type:'catalogue', logo: shopifyLogo, apiVersion: '2025-01', order: 'two-way', product: 'two-way', inventory: 'one-way', color: '#6AC301' },
  { label: 'WooCommerce', value: 'woocommerce', type: 'catalogue', logo: woocommerceLogo, apiVersion: '2.6', order: 'two-way', product: 'two-way', inventory: 'none', color: '#BAA2D6' },
  { label: 'Mirakl', value: 'mirakl', type: 'catalogue', logo: miraklLogo, apiVersion: 'Generic connector', order: 'two-way', product: 'none', inventory: 'none', color: '#003366' },
  { label: 'Magento', value: 'magento', type: 'catalogue', logo: magentoLogo, apiVersion: 'Generic connector', order: 'two-way', product: 'none', inventory: 'none', color: '#F46F25' },
  { label: 'Lightspeed', value: 'lightspeed', type: 'catalogue', logo: lightspeedLogo, apiVersion: 'Integration coming soon', order: 'none', product: 'none', inventory: 'none', color: '#E81C1C' },
  { label: 'Shopware', value: 'shopware', type: 'catalogue', logo: shopwareLogo, apiVersion: 'Integration coming soon', order: 'none', product: 'none', inventory: 'none', color: '#179EFF' },
  { label: 'Sinerlog', value: 'sinerlog', type: 'catalogue', logo: IconSinerlog, apiVersion: 'v2', order: 'two-way', product: 'one-way', inventory: 'none', color: '#057E5E', private: true}
]

const SERVICE_DESCRIPTIONS = {
  'directify': [
    { type: 'step', title: 'Enter a Directify space-ID or company-ID', body: 'This will send a connection request to the Directify space where you will be registered as a supplier with the products from this channel.' },
    { type: 'input', field: 'user', label: 'ID' }
  ],
  'shopify': [
    { type: 'step', title: 'Are you the store owner?', body: 'Please make sure you are the store owner, since only the store owner has the correct access to link Directify to the Shopify shop.' },
    { type: 'bool', defaultValue: true, field: 'managed', trueBody: 'I own all products in this webshop', falseBody: 'I own part of the webshop’s products', tooltip: 'If you dropship products to a client’s webshop Directify will make sure the product information will be pushed to the webshop as drafts. Furthermore, only the orders that contain your products will be visible in the fulfillment dashboard.' },
    { type: 'step', title: '1. Log into the Shopify webshop', body: 'Log into the webshop you want to link to Directify.' },
    { type: 'step', title: '2. Fill in the store name', body: 'Fill in the store name which can be found in Settings > Store Details > Store name.' },
    { type: 'input', field: 'user', label: 'Store name' },
    { type: 'step', title: '3. Go to App development', body: 'Go to Settings > Apps and sales channels > Develop apps.' },
    { type: 'step', title: '4. Create Directify as an app', body: 'Click on the ‘Create an app’ button and type in ‘Directify’ as the app name. Select yourself as the app developer.' },
    { type: 'step', title: '5. Fill in the API keys', body: 'Click on ‘API credentials’ and copy-paste the API key down below (the API key is visible and can easily be copy-pasted).' },
    { type: 'input', field: 'apiKey', label: 'API Key' },
    { type: 'step', title: '6. Configure Admin API scopes', body: 'Click on ‘Configure Admin API scopes’, here you can set the permissions Directifiy needs to work with Shopify.' },
    { type: 'step', title: '7. Set the right permissions', body: 'Give the following read and write permissions: ‘Assigned fulfillment orders’, ‘Fulfillment services’, ‘Inventory’, ‘Locations’, ‘Merchant-managed fulfillment orders’, ‘Orders’, ‘Products’, ‘Third-party fulfillment orders’ and ‘custom_fulfillment_services’' },
    { type: 'step', title: '8. Save and install the app', body: 'Click on ‘Save’ and then on ‘Install app’. When installed you enter a screen where you can reveal the Admin API access token once and fill it in down below. Do not save this token anywhere except here as it gives permissions to edit the entities selected above.' },
    { type: 'input', field: 'pass', label: 'Admin API access token' }
  ],
  'bolcom': [
    { title: '1. Go to “Client credentials voor de Retailer API“', body: 'You can find this in your Bol retailer account at instellingen -> diensten.' },
    { title: '2. Add a new Client credentials item', body: 'Click on the + Aanmaken button, a pop up with an input field will appear. Here you can fill in the name Directify.' },
    { title: '3. Copy & fill in the Client ID and API key', body: 'You will now see that Directify has been added, along with a Client ID. Copy this code and paste in the input field down below.' },
    { type: 'input', field: 'user', label: 'Client ID' },
    { type: 'input', field: 'pass', label: 'API Key' }
  ],
  'woocommerce': [
    { title: '1. Fill in the url to your webshop', body: 'Excluding "https://" or subpages' }, { type: 'input', field: 'baseUrl' }, // TODO clean in back-end
    { title: '2. Create an API key', body: 'Go to WooCommerce > Settings > Advanced > REST API. Write ‘Directify’ in the description, assign a user to the API key and choose ‘Read/write’ as permissions. Then click on ‘Generate API key’.' },
    { title: '3. Fill in the API keys', body: 'Copy the "Consumer Key" and "Consumer Secret" and enter them below' },
    { type: 'input', field: 'user', label: 'Consumer Key' },
    { type: 'input', field: 'pass', label: 'Consumer Secret' }
  ],
  'mirakl': [
    { title: '1. Fill in the url to your webshop', body: 'Excluding subpages' }, { type: 'input', field: 'baseUrl' }, // TODO clean in back-end
    { title: '2. Create an API key', body: 'Go to Mirakl > Profile > API-key. Then click on ‘Generate a new key’.' },
    { title: '3. Fill in the API keys', body: 'Copy the "API-key" and paste it below' },
    { type: 'input', field: 'apiKey', label: 'API Key' }
  ],
  'magento': [
    { title: '1. Fill in the url to your webshop', body: 'Excluding subpages' }, { type: 'input', field: 'baseUrl' }, // TODO clean in back-end
    { title: '2. Create an API key', body: 'Go to Magento > Profile > API-key. Then click on ‘Generate a new key’.' },
    { title: '3. Fill in the API keys', body: 'Copy the "API-key" and paste it below' },
    { type: 'input', field: 'apiKey', label: 'API Key' }
  ],
  'sinerlog': [
    { title: '1. Enter your Sinerlog Cross-commerce API-Key'}, { type: 'input', field: 'apiKey'},
    { title: '2. Enter your existing Sinerlog sales-channel id', body: 'This is your store-id in the Sinerlog system.'}, {type: 'input', field: 'channel_id'},
  ],
  'lightspeed': [
    { title: '1. Create API keys in you lightspeed account', body: 'Go to Settings > Store Settings > Developers.'},
    { title: '2. Copy the api_key, api_secret and cluster_url'},
    { type: 'input', field: 'user', label: 'api_key' },
    { type: 'input', field: 'pass', label: 'api_secret' },
    { type: 'input', field: 'baseUrl', label: 'cluster_url' },
  ],
  'shopware': [
    { title: '1. Create API keys in the Shopware sales-channel you want to connect', body: 'Go to the section labeled API Accessadmin panel in the sales channel configuration menu.'},
    { type: 'input', field: 'baseUrl', label: 'Store url' },
    { type: 'input', field: 'apiKey', label: 'Access key' },
  ]
}

  export default defineComponent({
    name: 'ChannelServiceLink',
    components: { TickIcon, CloseIcon, DButton, InfoButton },
    props: {
      modelValue: {
        type: Boolean,
        default: false
      },
      catalogue: {
        type: Object as PropType<CtCatalogue>,
        required: true
      }
    },
    emits: ['update:catalogue', 'update:modelValue', 'save', 'cancel'],
    setup (props, { emit }) {
      const $q = useQuasar()
      const $store = useStore()
      const $directr = $store.getters['Services/Directr']
      const $sys = $store.getters['Services/Sys']
      const tenant = ref<Tenant | null>(null)

      const getTenant = async () => {
        await $sys.get(`service/${ServiceType.CtrlService.Sys}/tenant`).then(result => {
          tenant.value = result.items?.[0]
        })
      }

      const services = ref<{ id: string, name: string, type: string, [key: string]: any }[]>([])

      const activeServices = computed(() => {
        return services.value.map(service => {
          if(!service?.active || !serviceOptions.value.find(o=> o.value === service.type)) return
          return {label: service.name, value: service.id}
        }
        ).filter(Boolean)
      })

      const serviceOptions = computed(() => {
        return SERVICE_TYPES.filter(service => service.type === props.catalogue.sys?.type && !service.private || (tenant.value?.settings?.private_services?.includes(service.value)))
      })
      onMounted(async () => {
        void getTenant()
        services.value = (await $sys.get('/service'))
      })

      const state = reactive({
        service: SERVICE_TYPES[0],
        isNextPressed: false,
        isExistingService: false,
        existingService: { label: '', value: '' }
      })

      const input = ref<Record<string, any>>({})

      const error = reactive({
        show: false,
        message: ''
      })

      function createConfig () {
        switch (state.service.value) {
          case 'sinerlog':
            if (!input.value?.apiKey) console.error('sinerlog missing apiKey', input.value)
            return {
              test: false,
              apiKey: input.value?.apiKey,
              meta: {
                channel_id: input.value.channel_id
              }
            }
          case 'bolcom':
            if (!input.value?.user || !input.value?.pass) console.error('bolcom missing user or pass', input.value)
            return (!input.value?.user || !input.value?.pass) ? false : {
              test: false,
              basicAuth: {
                user: input.value?.user,
                pass: input.value?.pass
              },
              apiKey: input.value?.apiKey
            }
          case 'shopify':
            return {
              test: false,
              basicAuth: {
                user: input.value?.user,
                pass: input.value?.pass
              },
              apiKey: input.value?.apiKey,
              meta: {
                managed: input.value?.managed === undefined ? true : input.value?.managed
              }
            }
          case 'woocommerce':
            return {
              test: false,
              baseUrl: input.value?.baseUrl,
              basicAuth: {
                user: input.value?.user,
                pass: input.value?.pass
              },
              apiKey: input.value?.apiKey
            }
          case 'magento':
            return {
              test: false,
              baseUrl: input.value?.baseUrl,
              basicAuth: {
                user: input.value?.user,
                pass: input.value?.pass
              },
              apiKey: input.value?.apiKey
            }
          case 'directify':
            return {
              tenant: input.value?.user,
              token: props.catalogue.token?.access_token,
              label: props.catalogue.name || input.value?.label
            }
          default:
            throw Error(`Cannot create service config, unknown service ${state.service.value}`)
        }
      }

      async function save () {
        try {
          $q.loading.show({ message: 'Linking to service...', spinner: QSpinnerTail, messageColor: 'white' })
          if (!state.service?.value) throw Error('Could not create service, no service selected')

          const syncObject = {
            inventory: { active: 0 },
            order: {
              active: 0,
              hooks: {
                [SyncActionType.Accepted]: {},
                [SyncActionType.Fulfilled]: {},
                [SyncActionType.Cancelled]: {}
              }
            },
            product: { active: 0 },
          }

          if (state.existingService.value) {
            emit('update:catalogue', {
              service: {
                id: state.existingService.value,
                name: state.existingService.value,
                sync: syncObject
              }
            })
          } else {
            const id = `${state.service.value}_${props.catalogue.id}-${props.catalogue.name}-${state.service.label}`

            const config = createConfig()

            if (!config) throw Error(`Could not create service, failed to create config for service ${state.service.value}`)

            // separate handling for directify <> directify link
            if (state.service.value === 'directify') {
              let access_token = config.token

              if (!config.token) {
                access_token = `${CHANNEL_API_KEY_PRE_ID}-${CryptoJS.SHA256(String(Math.round(Math.random())+Date.now()))}`
                // create channel token & save

                const updated = await $directr.patch('catalogue', props.catalogue.sys.id, { token: { access_token, scope: 'channel' } }, {})
                config.token = access_token
              }

              const created = await $sys.put('/auth/connection_requests/direct', {}, { ...config, catalogue: props.catalogue.sys.id, label: null }).catch(console.error)
              if (!created || created?.error) throw Error(`Directify connection request failed${created?.error ? `: ${created.error}` : ''}`)

              emit('update:catalogue', {
                service: {
                  id: created.serviceId,
                  name: config.label,
                  sync: {
                    inventory: { active: 2 },
                    product: { active: 2 },
                    order: {
                      active: 3,
                      hooks: {
                        [SyncActionType.Created]: {},
                        [SyncActionType.Accepted]: {},
                        [SyncActionType.Fulfilled]: {},
                        [SyncActionType.Cancelled]: {},
                        [SyncActionType.Issue]: {},
                        [SyncActionType.Issue_resolved]: {},
                        [SyncActionType.Transit]: {},
                        [SyncActionType.OnHold]: {},
                        [SyncActionType.Rejected]: {},
                        [SyncActionType.Picked]: {}
                      }
                    },
                  }
                }
              })
            } else {
              const created = await $sys.services.create(state.service.value, id, {
                name: `${state.service.value} ${config.baseUrl || config.basicAuth?.user}`,
                active: true,
                config
              })

              if (!created) throw Error('service.create returned undefined')

              emit('update:catalogue', {
                service: {
                  id: created.id,
                  name: created.name,
                  sync: syncObject
                }
              })
            }
          }

          emit('update:modelValue', false)
          state.isNextPressed = false
          $q.notify({ type: 'positive', message: state.service.value === 'directify' ? `Connection request made` : `App ${state.service?.label} Connected` })
          resetModal()
          input.value = {}
        } catch (e) {
          $q.loading.hide()
          console.error(e)
          error.message = `Could not connect to ${state.service?.label || 'unknown service'} with given credential, please try again`
          error.show = true
          setTimeout(() => error.show = false, 10000)
        } finally {
          $q.loading.hide()
        }
      }

      function cancel () {
        resetModal()
        emit('cancel')
        emit('update:modelValue', false)
      }

      function next () {
        if (state.service)
          state.isNextPressed = true
        input.value = {}
      }

      function back () {
        if (state.service) {
          state.isNextPressed = false
          state.existingService = { label: '', value: '' }
          input.value = {}
        }

      }

      function handleExistingServiceSelect (event) {
        state.existingService = event
      }

      function selectService (app: any) {
        state.existingService = { label: '', value: '' }
        state.service = app
      }

      function resetModal () {
        input.value = {}
        state.existingService = { label: '', value: '' }
        state.isNextPressed = false
        state.service = SERVICE_TYPES[0]
        state.isExistingService = false
      }

      return {
        ...toRefs(state),
        services,
        serviceOptions,
        activeServices,
        Status,
        format,
        error,
        input,
        cancel,
        save,
        next,
        back,
        resetModal,
        handleExistingServiceSelect,
        selectService,
        SERVICE_TYPES,
        SERVICE_DESCRIPTIONS
      }
    },

  })
